// src/pages/analytics/TransfersTab.tsx
//
// Transfer exceptions (recipe transfer-exceptions, flag analytics.transfers,
// agent 3.21.0+) — warehouse transfers from Advantage TransferInvt /
// StagingTransferInvt that need a look:
//   item            — the item received (ToItemNo) isn't the item sent (ItemNo)
//   qty             — received qty (ToQty) ≠ sent qty (TransferQTY)
//   staging_missing — transmitted from staging, never posted
//   staging_qty     — posted with a different qty than staging transmitted
//   unposted        — batch still not posted after N days (one row per batch)
// $ impact is at average cost, signed (negative = loss).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, RefreshCw, Download, FileSpreadsheet, AlertTriangle, RotateCcw, ArrowRight } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import type { TransferException, TransferExceptionType } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterSelect } from './salesFilterBar';
import { subcategoryOptions } from './itemSubcategories';
import { AnalyticsSavedViews } from './filterPane';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(v);
const DAYS = [30, 90, 180, 365];
const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;
const btn = 'px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40';

type TypeFilter = 'all' | 'item' | 'qty' | 'staging' | 'unposted';
const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: 'all',      label: 'All' },
  { value: 'item',     label: 'Item swaps' },
  { value: 'qty',      label: 'Qty differences' },
  { value: 'staging',  label: 'Staging vs posted' },
  { value: 'unposted', label: 'Not posted' },
];

const TYPE_META: Record<TransferExceptionType, { label: string; cls: string }> = {
  item:            { label: 'Item swap',        cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  qty:             { label: 'Qty diff',         cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  staging_missing: { label: 'Not in posted',    cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  staging_qty:     { label: 'Staging qty diff', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  unposted:        { label: 'Not posted',       cls: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
};

const DEFAULTS = { days: 90, fromWh: '', toWh: '', type: 'all' as TypeFilter, itemCategory: '', itemSubcategory: '' };

const COLS: CsvColumns<TransferException> = [
  ['Type',             (e) => TYPE_META[e.type]?.label ?? e.type],
  ['Batch',            (e) => e.batchNo],
  ['Date',             (e) => e.date],
  ['From WH',          (e) => e.fromWh],
  ['To WH',            (e) => e.toWh],
  ['Item Sent',        (e) => e.itemNo],
  ['Sent Description', (e) => e.itemDesc],
  ['Item Received',    (e) => e.toItemNo],
  ['Received Description', (e) => e.toItemDesc],
  ['Qty Sent',         (e) => e.qty],
  ['Qty Received',     (e) => e.toQty],
  ['Diff Qty',         (e) => e.diffQty],
  ['$ Impact',         (e) => e.value],
  ['Category',         (e) => e.category],
  ['Subcategory',      (e) => e.subCategory],
  ['Posted',           (e) => (e.posted ? 'Yes' : 'No')],
  ['Age (days)',       (e) => e.ageDays],
  ['Note',             (e) => e.note],
];

function Tile({ label, value, sub, tone = 'default', headline = false }: {
  label: string; value: string; sub?: string; tone?: 'default' | 'bad' | 'warn'; headline?: boolean;
}) {
  const color = tone === 'bad' ? 'text-red-300' : tone === 'warn' ? 'text-amber-300' : 'text-white';
  return (
    <div className={`p-3 rounded-xl border ${headline ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-800/30 border-slate-700/40'}`}>
      <div className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</div>
      <div className={`${headline ? 'text-2xl' : 'text-xl'} font-semibold tabular-nums ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function TransfersTab() {
  const [days, setDays] = useState(DEFAULTS.days);
  const [fromWh, setFromWh] = useState(DEFAULTS.fromWh);
  const [toWh, setToWh] = useState(DEFAULTS.toWh);
  const [type, setType] = useState<TypeFilter>(DEFAULTS.type);
  const [itemCategory, setItemCategory] = useState(DEFAULTS.itemCategory);
  const [itemSubcategory, setItemSubcategory] = useState(DEFAULTS.itemSubcategory);
  const reset = () => {
    setDays(DEFAULTS.days); setFromWh(DEFAULTS.fromWh); setToWh(DEFAULTS.toWh);
    setType(DEFAULTS.type); setItemCategory(DEFAULTS.itemCategory); setItemSubcategory(DEFAULTS.itemSubcategory);
  };

  const { data: filters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
  });
  const subcats = subcategoryOptions(filters, itemCategory);

  const q = useQuery({
    queryKey: ['analyticsTransfers', days, fromWh, toWh, type, itemCategory, itemSubcategory],
    queryFn: () => api.getTransferExceptions({ days, fromWh, toWh, type, itemCategory, itemSubcategory }),
    staleTime: 60_000,
    retry: 1,
  });
  const data = q.data;
  const rows = data?.exceptions ?? [];
  const s = data?.summary;
  const whs = (data?.warehouses ?? []).map(w => ({ value: String(w), label: `WH ${w}` }));
  const file = 'transfer-exceptions';

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><ArrowLeftRight size={20} /> Transfers</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Warehouse transfers that don't line up: wrong item received, quantity differences, staging vs posted, and batches not posted.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Days</span>
          <div className="flex items-center gap-1.5">
            {DAYS.map(d => <button key={d} onClick={() => setDays(d)} className={pill(days === d)}>{d}</button>)}
          </div>
        </div>
        <FilterSelect label="From WH" value={fromWh} onChange={setFromWh} options={whs} />
        <FilterSelect label="To WH" value={toWh} onChange={setToWh} options={whs} />
        {(filters?.itemCategories?.length ?? 0) > 0 && (
          <FilterSelect label="Item Category" value={itemCategory} onChange={v => { setItemCategory(v); setItemSubcategory(''); }}
            options={(filters?.itemCategories ?? []).map(c => ({ value: c, label: c }))} />
        )}
        {subcats && (
          <FilterSelect label="Item Subcategory" value={itemSubcategory} onChange={setItemSubcategory}
            options={subcats.map(c => ({ value: c, label: c }))} />
        )}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Show</span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {TYPE_FILTERS.map(t => <button key={t.value} onClick={() => setType(t.value)} className={pill(type === t.value)}>{t.label}</button>)}
          </div>
        </div>
        <div className="flex-1" />
        <AnalyticsSavedViews
          page="transfers"
          getState={() => ({ days, fromWh, toWh, type, itemCategory, itemSubcategory })}
          applyState={(v) => {
            if (typeof v.days === 'number' && DAYS.includes(v.days)) setDays(v.days);
            if (typeof v.fromWh === 'string') setFromWh(v.fromWh);
            if (typeof v.toWh === 'string') setToWh(v.toWh);
            if (typeof v.type === 'string' && TYPE_FILTERS.some(t => t.value === v.type)) setType(v.type as TypeFilter);
            if (typeof v.itemCategory === 'string') setItemCategory(v.itemCategory);
            if (typeof v.itemSubcategory === 'string') setItemSubcategory(v.itemSubcategory);
          }}
        />
        <button onClick={reset} title="Reset filters" className={btn}><RotateCcw size={11} /> Reset</button>
        <button onClick={() => exportCsv(file, COLS, rows)} disabled={rows.length === 0} className={btn}><Download size={11} /> CSV</button>
        <button onClick={() => exportXlsx(file, COLS, rows)} disabled={rows.length === 0} className={btn}><FileSpreadsheet size={11} /> Excel</button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> Couldn't load transfers: {(q.error as Error).message}
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : data && !data.available ? (
        <div className="p-8 rounded-xl bg-slate-800/20 border border-slate-700/30 text-center text-slate-400 text-sm">
          No transfer data — needs Advantage TransferInvt (agent 3.21.0+).
        </div>
      ) : data && s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Tile label="Item swaps" value={fmtNum(s.itemSwaps)} sub="received ≠ sent item" tone={s.itemSwaps > 0 ? 'bad' : 'default'} headline />
            <Tile label="Qty differences" value={fmtNum(s.qtyDiffs)} sub="received ≠ sent qty" tone={s.qtyDiffs > 0 ? 'warn' : 'default'} />
            <Tile label="Staging issues" value={fmtNum(s.stagingMissing + s.stagingQtyDiffs)}
              sub={`${fmtNum(s.stagingMissing)} not posted · ${fmtNum(s.stagingQtyDiffs)} qty`} tone={s.stagingMissing + s.stagingQtyDiffs > 0 ? 'warn' : 'default'} />
            <Tile label="Unposted batches" value={fmtNum(s.unpostedBatches)} tone={s.unpostedBatches > 0 ? 'warn' : 'default'} />
            <Tile label="$ impact" value={fmtCur(s.valueImpact)} sub="at average cost" tone={s.valueImpact < 0 ? 'bad' : 'default'} />
            <Tile label="Transfers" value={fmtNum(s.batches)} sub={`${fmtNum(s.lines)} lines · ${fmtNum(s.qtySent)} units`} />
          </div>
          <p className="text-[11px] text-slate-500">{data.dateFrom} → {data.dateTo}</p>

          {data.byRoute.length > 0 && (
            <div className="rounded-xl border border-slate-700/40 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-800/60">
                  <tr className="text-slate-400">
                    <th className="px-3 py-2 text-left font-medium">Route</th>
                    <th className="px-3 py-2 text-right font-medium">Batches</th>
                    <th className="px-3 py-2 text-right font-medium">Lines</th>
                    <th className="px-3 py-2 text-right font-medium">Exceptions</th>
                    <th className="px-3 py-2 text-right font-medium">% of lines</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byRoute.map(r => (
                    <tr key={`${r.fromWh}-${r.toWh}`} className="border-t border-slate-800/60 hover:bg-slate-700/20 cursor-pointer"
                      onClick={() => { setFromWh(String(r.fromWh)); setToWh(String(r.toWh)); }} title="Filter to this route">
                      <td className="px-3 py-1.5 text-white">WH {r.fromWh} <ArrowRight size={11} className="inline mx-1 text-slate-500" /> WH {r.toWh}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(r.batches)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(r.lines)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${r.exceptions > 0 ? 'text-amber-300' : 'text-slate-500'}`}>{fmtNum(r.exceptions)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">{r.lines ? `${((r.exceptions / r.lines) * 100).toFixed(1)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="rounded-xl border border-slate-700/40 overflow-x-auto max-h-[65vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-800">
                <tr className="text-slate-400">
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Batch</th>
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Route</th>
                  <th className="px-3 py-2 text-left font-medium">Item sent</th>
                  <th className="px-3 py-2 text-left font-medium">Item received</th>
                  <th className="px-3 py-2 text-right font-medium">Sent</th>
                  <th className="px-3 py-2 text-right font-medium">Received</th>
                  <th className="px-3 py-2 text-right font-medium">Diff</th>
                  <th className="px-3 py-2 text-right font-medium">$ impact</th>
                  <th className="px-3 py-2 text-left font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e, i) => {
                  const meta = TYPE_META[e.type] ?? { label: e.type, cls: 'text-slate-300 border-slate-600' };
                  const swapped = e.type === 'item';
                  return (
                    <tr key={`${e.type}-${e.batchNo}-${e.itemNo}-${i}`} className="border-t border-slate-800/50 hover:bg-slate-700/20 align-top">
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className="px-3 py-1.5 text-slate-300 tabular-nums">{e.batchNo}</td>
                      <td className="px-3 py-1.5 text-slate-300 whitespace-nowrap">
                        {e.date}
                        {!e.posted && e.ageDays > 0 && <div className="text-[10px] text-slate-500">{e.ageDays}d unposted</div>}
                      </td>
                      <td className="px-3 py-1.5 text-slate-300 whitespace-nowrap">{e.fromWh} → {e.toWh}</td>
                      <td className="px-3 py-1.5">
                        {e.itemNo ? <>
                          <div className="text-white">{e.itemNo}</div>
                          <div className="text-[10px] text-slate-500 max-w-[220px] truncate" title={e.itemDesc}>{e.itemDesc}</div>
                        </> : <span className="text-slate-500">—</span>}
                      </td>
                      <td className={`px-3 py-1.5 ${swapped ? 'bg-red-500/10' : ''}`}>
                        {e.toItemNo ? <>
                          <div className={swapped ? 'text-red-300 font-medium' : 'text-white'}>{e.toItemNo}</div>
                          <div className="text-[10px] text-slate-500 max-w-[220px] truncate" title={e.toItemDesc}>{e.toItemDesc}</div>
                        </> : <span className="text-slate-500">—</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">{fmtNum(e.qty)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">{fmtNum(e.toQty)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${e.diffQty < 0 ? 'text-red-300' : e.diffQty > 0 ? 'text-amber-300' : 'text-slate-500'}`}>
                        {e.diffQty ? fmtNum(e.diffQty) : ''}
                      </td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${e.value < 0 ? 'text-red-300' : 'text-slate-300'}`}>{e.value ? fmtCur(e.value) : ''}</td>
                      <td className="px-3 py-1.5 text-slate-500">{e.note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === 0 && <div className="text-center py-6 text-slate-500 text-sm">No exceptions in this period</div>}
          </div>
          {data.truncated && <p className="text-[11px] text-amber-400">Showing the first {rows.length} exceptions; narrow the filters to see the rest.</p>}
        </>
      )}
    </div>
  );
}
